"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useEmblaCarousel from "embla-carousel-react";
import { LIVE_STATUSES } from "@/contants";
import { 
  formatHandicapLabel, 
  formatStakeDateTime, 
  makeTeamCode, 
  reverseHandicapLabel 
} from "@/utils/format";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { useMatches } from "@/hooks/useMatches";
import { getMatchDisplayTime, getLivePhaseLabel } from "@/utils/matchInfo";
import StakeOutcomeOddsButton from "./StakeOutcomeOddsButton";
import StakeOtherBetsDropdown from "../StakeOtherBetsDropdown";
import StakeLoading from "../StakeLoading";
import ICON_CORNER from "@/assets/images/corner.svg";
import ICON_ARROW_UP from "@/assets/images/arrow-up.svg";

export default function StakeMatch({ match }) {
  const [, setClockTick] = useState(0);
  const [openOtherBets, setOpenOtherBets] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: 1,
    loop: false,
  });

  const onSelect = useCallback((api) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect(emblaApi);
  }, [emblaApi, onSelect]);

  const { getScore, getOdds } = useLiveDataForMatch(match.id);
  const liveScore = getScore();
  const liveOdds = getOdds();
  const { getMatchOdds } = useMatches();

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const statusNum =
    liveScore?.status !== undefined ? Number(liveScore.status) : Number(match.status);
  const isLive = LIVE_STATUSES.includes(statusNum);

  useEffect(() => {
    if (!isLive) return undefined;
    const timerId = setInterval(() => {
      setClockTick((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [isLive]);

  const liveScoreForClock = useMemo(() => {
    if (!isLive || !liveScore) return liveScore;
    const { minute, currentMinute, ...rest } = liveScore;
    return rest;
  }, [isLive, liveScore]);

  const { weekdayDate, time } = formatStakeDateTime(match?.matchTime);
  const staticDisplayTime = getMatchDisplayTime(liveScore, statusNum, match?.matchTime, match?.halfStartTime);
  const clockDisplayTime = getMatchDisplayTime(liveScoreForClock, statusNum, match?.matchTime, match?.halfStartTime);
  const displayTime = clockDisplayTime?.type === "minute" ? clockDisplayTime : staticDisplayTime;

  const homeCode = makeTeamCode(match?.homeName);
  const awayCode = makeTeamCode(match?.awayName);
  const homeHandicap = formatHandicapLabel(handicapValue, true) || "0";
  const awayHandicap = reverseHandicapLabel(homeHandicap);
  const otherOddsCount = (match?.countOtherOdds ?? 0) > 0 ? (match?.countOtherOdds - 1) : 0;
  const livePhaseLabel = getLivePhaseLabel(statusNum);

  const handleOtherBetsButtonClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenOtherBets((prev) => !prev);
    if (!openOtherBets && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  return (
    <div className="stake-bg-header px-3 py-3">
      {/* Mobile stacked layout */}
      <div className="flex flex-col gap-3 md:hidden">
        {/* Date/Time + market count */}
        <div className="flex min-h-[24px] items-center gap-3 overflow-hidden text-[var(--theme-text)]">
          <span className="text-sm whitespace-nowrap">{weekdayDate}</span>
          <span className="text-sm whitespace-nowrap">{time}</span>
          {isLive && (
            <div className="flex items-center gap-1.5">
              {displayTime.type === "var" && (
                <span className="rounded-full bg-vibrant-red px-2 py-0.5 text-[10px] font-extrabold uppercase text-white">
                  VAR
                </span>
              )}
              {displayTime.type === "minute" && (
                <>
                  <span className="rounded bg-vibrant-red px-1.5 py-0.5 text-[10px] font-extrabold uppercase text-white">
                    {livePhaseLabel}
                  </span>
                  <span className="rounded bg-orange-500 px-1.5 py-0.5 text-[11px] font-bold text-white">
                    {displayTime.content}<span className="animate-[blink_1s_infinite]">&apos;</span>
                  </span>
                  <span className="flex items-center gap-1 rounded bg-[var(--stake-active-bg)] px-1.5 py-0.5 text-[11px] font-semibold text-white">
                    <img src={ICON_CORNER} alt="corner" width={10} height={10} className="shrink-0 brightness-0 invert" />
                    {currentHomeCorner ?? 0}-{currentAwayCorner ?? 0}
                  </span>
                </>
              )}
              {displayTime.type === "status" && (
                <span className="rounded-full bg-vibrant-red px-2 py-0.5 text-[10px] font-extrabold uppercase text-white">
                  {displayTime.content}
                </span>
              )}
            </div>
          )}
          {otherOddsCount > 0 && (
            <button
              type="button"
              onClick={handleOtherBetsButtonClick}
              className="ml-auto flex items-center gap-1.5 text-base font-semibold text-[var(--theme-text)] cursor-pointer bg-transparent border-none hover:opacity-80 transition-opacity"
            >
              <span>+{otherOddsCount}</span>
              <img
                src={ICON_ARROW_UP}
                alt=""
                width={16}
                height={16}
                className={`brightness-0 invert transition-transform duration-200 ${openOtherBets ? "rotate-180" : ""}`}
              />
            </button>
          )}
        </div>

        {/* Teams stacked */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 truncate">
            <img
              src={match?.homeTeam?.logo || match?.homeLogo}
              alt={match?.homeName || "home"}
              className="h-5 w-5 shrink-0 rounded-full object-contain"
              loading="lazy"
            />
            <span className="truncate text-sm font-semibold text-[var(--theme-text)]">
              {match?.homeName}
            </span>
          </div>
          <div className="flex items-center gap-2 truncate">
            <img
              src={match?.awayTeam?.logo || match?.awayLogo}
              alt={match?.awayName || "away"}
              className="h-5 w-5 shrink-0 rounded-full object-contain"
              loading="lazy"
            />
            <span className="truncate text-sm font-semibold text-[var(--theme-text)]">
              {match?.awayName}
            </span>
          </div>
        </div>

        {/* Carousel for Odds */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {/* Slide 1: Hiệp 1 */}
            <div className="flex-[0_0_100%] min-w-0">
              <div className="text-center text-[11px] font-bold text-[var(--theme-text-muted)] uppercase mb-2">Hiệp 1</div>
              <div className="grid grid-cols-3 gap-1">
                <div className="flex flex-col gap-1">
                  <StakeOutcomeOddsButton
                    label={formatHandicapLabel(handicapHalfValue, true) || "0"}
                    odds={homeHandicapHalfOdds}
                    trend={homeHandicapHalfTrend}
                  />
                  <StakeOutcomeOddsButton
                    label=""
                    odds={awayHandicapHalfOdds}
                    trend={awayHandicapHalfTrend}
                  />
                  <StakeOutcomeOddsButton label="" odds="-" />
                </div>
                <div className="flex flex-col gap-1">
                  <StakeOutcomeOddsButton
                    label={`O (${overUnderHalfValue || "-"})`}
                    odds={overHalfOdds}
                    trend={overHalfTrend}
                  />
                  <StakeOutcomeOddsButton
                    label={`U (${overUnderHalfValue || "-"})`}
                    odds={underHalfOdds}
                    trend={underHalfTrend}
                  />
                  <StakeOutcomeOddsButton label="" odds="-" />
                </div>
                <div className="flex flex-col gap-1">
                  <StakeOutcomeOddsButton
                    label="1"
                    odds={homeEuropeHalfOdds}
                    trend={homeEuropeHalfTrend}
                  />
                  <StakeOutcomeOddsButton
                    label="X"
                    odds={drawHalfOdds}
                    trend={drawHalfTrend}
                  />
                  <StakeOutcomeOddsButton
                    label="2"
                    odds={awayEuropeHalfOdds}
                    trend={awayEuropeHalfTrend}
                  />
                </div>
              </div>
            </div>

            {/* Slide 2: Toàn trận */}
            <div className="flex-[0_0_100%] min-w-0">
              <div className="text-center text-[11px] font-bold text-[var(--theme-text-muted)] uppercase mb-2">Toàn trận</div>
              <div className="grid grid-cols-3 gap-1">
                <div className="flex flex-col gap-1">
                  <StakeOutcomeOddsButton
                    label={formatHandicapLabel(handicapValue, true) || "0"}
                    odds={homeHandicapOdds}
                    trend={homeHandicapTrend}
                  />
                  <StakeOutcomeOddsButton
                    label=""
                    odds={awayHandicapOdds}
                    trend={awayHandicapTrend}
                  />
                  <StakeOutcomeOddsButton label="" odds="-" />
                </div>
                <div className="flex flex-col gap-1">
                  <StakeOutcomeOddsButton
                    label={`O (${overUnderValue || "-"})`}
                    odds={overOdds}
                    trend={overTrend}
                  />
                  <StakeOutcomeOddsButton
                    label={`U (${overUnderValue || "-"})`}
                    odds={underOdds}
                    trend={underTrend}
                  />
                  <StakeOutcomeOddsButton label="" odds="-" />
                </div>
                <div className="flex flex-col gap-1">
                  <StakeOutcomeOddsButton
                    label="1"
                    odds={homeEuropeOdds}
                    trend={homeEuropeTrend}
                  />
                  <StakeOutcomeOddsButton
                    label="X"
                    odds={instantDrawOdds}
                    trend={drawTrend}
                  />
                  <StakeOutcomeOddsButton
                    label="2"
                    odds={awayEuropeOdds}
                    trend={awayEuropeTrend}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel Dots */}
        <div className="flex justify-center gap-1.5 mt-1">
          {[0, 1].map((index) => (
             <div
               key={index}
               className={`h-1.5 w-1.5 rounded-full transition-colors ${
                 selectedIndex === index ? "bg-[var(--theme-text)]" : "bg-[var(--theme-border)]"
               }`}
             />
          ))}
        </div>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden md:grid [grid-template-columns:31%_61%_8%] grid-rows-[auto_auto] gap-x-2 gap-y-1">
        <div className="col-start-1 row-start-1 flex min-h-[24px] items-center gap-3 overflow-hidden text-[var(--theme-text)]">
          <span className="text-sm whitespace-nowrap">{weekdayDate}</span>
          <span className="text-sm whitespace-nowrap">{time}</span>
          {isLive && (
            <div className="flex items-center gap-1.5">
              {displayTime.type === "var" && (
                <span className="rounded-full bg-vibrant-red px-2 py-0.5 text-[10px] font-extrabold uppercase text-white">
                  VAR
                </span>
              )}
              {displayTime.type === "minute" && (
                <>
                  <span className="rounded bg-vibrant-red px-1.5 py-0.5 text-[10px] font-extrabold uppercase text-white">
                    {livePhaseLabel}
                  </span>
                  <span className="rounded bg-orange-500 px-1.5 py-0.5 text-[11px] font-bold text-white">
                    {displayTime.content}<span className="animate-[blink_1s_infinite]">&apos;</span>
                  </span>
                  <span className="flex items-center gap-1 rounded bg-[var(--stake-active-bg)] px-1.5 py-0.5 text-[11px] font-semibold text-white">
                    <img src={ICON_CORNER} alt="corner" width={10} height={10} className="shrink-0 brightness-0 invert" />
                    {currentHomeCorner ?? 0}-{currentAwayCorner ?? 0}
                  </span>
                </>
              )}
              {displayTime.type === "status" && (
                <span className="rounded-full bg-vibrant-red px-2 py-0.5 text-[10px] font-extrabold uppercase text-white">
                  {displayTime.content}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="col-start-2 row-start-1">
          <div className="grid grid-cols-2 gap-2">
            <div className="text-center text-[11px] font-bold text-[var(--theme-text-muted)] uppercase tracking-wider">Hiệp 1</div>
            <div className="text-center text-[11px] font-bold text-[var(--theme-text-muted)] uppercase tracking-wider">Toàn trận</div>
          </div>
        </div>

        <div className="col-start-1 col-end-4 row-start-2 border-y border-[var(--theme-border)] py-2">
          <div className="grid [grid-template-columns:31%_62%_8%] gap-x-2">
            <div className="col-start-1 pr-2 min-w-0 flex items-center">
              <div className="grid w-full grid-rows-2 gap-2">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="flex flex-1 min-w-0 items-center gap-2 overflow-hidden">
                    <img
                      src={match?.homeTeam?.logo || match?.homeLogo}
                      alt={match?.homeName || "home"}
                      className="h-5 w-5 shrink-0 rounded-full object-contain"
                      loading="lazy"
                    />
                    <span className="truncate text-base font-semibold text-[var(--theme-text)]">
                      {match?.homeName}
                    </span>
                  </div>
                  <span className="shrink-0 text-[14px] font-bold text-[var(--theme-text)]">{currentHomeScore ?? 0}</span>
                </div>

                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="flex flex-1 min-w-0 items-center gap-2 overflow-hidden">
                    <img
                      src={match?.awayTeam?.logo || match?.awayLogo}
                      alt={match?.awayName || "away"}
                      className="h-5 w-5 shrink-0 rounded-full object-contain"
                      loading="lazy"
                    />
                    <span className="truncate text-base font-semibold text-[var(--theme-text)]">
                      {match?.awayName}
                    </span>
                  </div>
                  <span className="shrink-0 text-[14px] font-bold text-[var(--theme-text)]">{currentAwayScore ?? 0}</span>
                </div>
              </div>
            </div>

            <div className="col-start-2">
              <div className="grid grid-cols-2 gap-2">
                {/* HT Odds */}
                <div className="grid grid-cols-3 gap-1">
                  <div className="flex flex-col gap-1">
                    <StakeOutcomeOddsButton
                      label={`${homeCode} (${formatHandicapLabel(handicapHalfValue, true) || "0"})`}
                      odds={homeHandicapHalfOdds}
                      trend={homeHandicapHalfTrend}
                    />
                    <StakeOutcomeOddsButton
                      label={`${awayCode} (${reverseHandicapLabel(formatHandicapLabel(handicapHalfValue, true)) || "0"})`}
                      odds={awayHandicapHalfOdds}
                      trend={awayHandicapHalfTrend}
                    />
                    <StakeOutcomeOddsButton label="" odds="-" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <StakeOutcomeOddsButton
                      label={`O (${overUnderHalfValue || "-"})`}
                      odds={overHalfOdds}
                      trend={overHalfTrend}
                    />
                    <StakeOutcomeOddsButton
                      label={`U (${overUnderHalfValue || "-"})`}
                      odds={underHalfOdds}
                      trend={underHalfTrend}
                    />
                    <StakeOutcomeOddsButton label="" odds="-" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <StakeOutcomeOddsButton
                      label="1"
                      odds={homeEuropeHalfOdds}
                      trend={homeEuropeHalfTrend}
                    />
                    <StakeOutcomeOddsButton
                      label="X"
                      odds={drawHalfOdds}
                      trend={drawHalfTrend}
                    />
                    <StakeOutcomeOddsButton
                      label="2"
                      odds={awayEuropeHalfOdds}
                      trend={awayEuropeHalfTrend}
                    />
                  </div>
                </div>

                {/* FT Odds */}
                <div className="grid grid-cols-3 gap-1">
                  <div className="flex flex-col gap-1">
                    <StakeOutcomeOddsButton
                      label={`${homeCode} (${homeHandicap})`}
                      odds={homeHandicapOdds}
                      trend={homeHandicapTrend}
                    />
                    <StakeOutcomeOddsButton
                      label={`${awayCode} (${awayHandicap})`}
                      odds={awayHandicapOdds}
                      trend={awayHandicapTrend}
                    />
                    <StakeOutcomeOddsButton label="" odds="-" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <StakeOutcomeOddsButton
                      label={`O (${overUnderValue || "-"})`}
                      odds={overOdds}
                      trend={overTrend}
                    />
                    <StakeOutcomeOddsButton
                      label={`U (${overUnderValue || "-"})`}
                      odds={underOdds}
                      trend={underTrend}
                    />
                    <StakeOutcomeOddsButton label="" odds="-" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <StakeOutcomeOddsButton
                      label="1"
                      odds={homeEuropeOdds}
                      trend={homeEuropeTrend}
                    />
                    <StakeOutcomeOddsButton
                      label="X"
                      odds={instantDrawOdds}
                      trend={drawTrend}
                    />
                    <StakeOutcomeOddsButton
                      label="2"
                      odds={awayEuropeOdds}
                      trend={awayEuropeTrend}
                    />
                  </div>
                </div>
              </div>
            </div>

            {otherOddsCount > 0 && (
              <button
                type="button"
                onClick={handleOtherBetsButtonClick}
                className="col-start-3 min-w-[30px] self-center justify-self-start  p-0 flex items-center gap-1.5 text-base font-semibold text-[var(--theme-text)] cursor-pointer bg-transparent border-none hover:opacity-80 transition-opacity"
              >
                <span>+{otherOddsCount}</span>
                <img
                  src={ICON_ARROW_UP}
                  alt=""
                  width={16}
                  height={16}
                  className={`brightness-0 invert transition-transform duration-200 ${openOtherBets ? "rotate-180" : ""}`}
                />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Other bets dropdown */}
      {otherOddsCount > 0 && openOtherBets && (
        <div className="mt-3">
          {loadingOtherOdds && !otherOddsData && (
            <div className="py-4 flex justify-center">
              <StakeLoading />
            </div>
          )}
          {!loadingOtherOdds && otherOddsData && (
            <StakeOtherBetsDropdown
              goalOddEvenOdds={otherOddsData?.goalOddEvenOdds}
              correctScoresOdds={otherOddsData?.correctScoresOdds}
              totalCornersOdds={otherOddsData?.totalCornersOdds}
              correctScoresHalfOdds={otherOddsData?.correctScoresHalfOdds}
              totalCornersHalfOdds={otherOddsData?.totalCornersHalfOdds}
            />
          )}
        </div>
      )}
    </div>
  );
}
